import { useState, useEffect, useCallback } from "react";

const navLinks = [
  { label: "About A&J", href: "#intro" },
  { label: "What We Do", href: "#what-we-do" },
  { label: "Platforms & Initiatives", href: "#platforms" },
  { label: "Legacy", href: "#legacy" },
  { label: "Partnerships", href: "#partners" },
  { label: "Contact", href: "#contact" },
];

const LOGO_URL =
  "https://storage.helloreaddy.io/project_files/3695ba64-190a-4f8e-a9e2-04b761fa00c1/9bbf6cf8-b106-4501-9333-f0d6926a8c4e_compressed_1631335680439.webp";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full flex items-center justify-between px-4 md:px-8 lg:px-12 py-4">
        {/* Logo */}
        <button
          onClick={scrollToTop}
          className="flex items-center gap-3 cursor-pointer"
          type="button"
        >
          <img
            src={LOGO_URL}
            alt="Amin & Jahan Corporation Ltd."
            className="h-9 w-auto object-contain"
          />
          <span
            className={`font-heading font-bold text-sm md:text-base tracking-tight hidden sm:block transition-colors duration-300 ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            Amin &amp; Jahan Corporation Ltd.
          </span>
        </button>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-700 hover:text-primary-600"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
            scrolled ? "text-foreground-800" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? (
            <i className="ri-close-line text-xl" />
          ) : (
            <i className="ri-menu-line text-xl" />
          )}
        </button>
      </nav>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-sm border-b border-background-200/70 px-4 pb-6">
          <ul className="flex flex-col gap-1 pt-2">
            <li>
              <button
                type="button"
                onClick={scrollToTop}
                className="w-full text-left px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors"
              >
                Home
              </button>
            </li>
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}